import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useRole } from "@/lib/roles";
import { ShieldCheck, Loader2, Save, AlertTriangle } from "lucide-react";

const HEALTH_LABEL = {
  HEALTHY: { txt: "Operacional", cls: "text-emerald-700 bg-emerald-50 border-emerald-200", dot: "bg-emerald-500" },
  DEGRADED: { txt: "Canal degradado", cls: "text-amber-700 bg-amber-50 border-amber-200", dot: "bg-amber-500" },
  PAUSED: { txt: "Envios pausados", cls: "text-zinc-700 bg-zinc-100 border-zinc-300", dot: "bg-zinc-500" },
  BLOCKED: { txt: "Envios pausados", cls: "text-zinc-700 bg-zinc-100 border-zinc-300", dot: "bg-zinc-500" },
  DISCONNECTED: { txt: "Desligado", cls: "text-red-700 bg-red-50 border-red-200", dot: "bg-red-500" },
};

function Toggle({ checked, onChange, disabled, testid }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      data-testid={testid}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${checked ? "bg-zinc-900" : "bg-zinc-300"} ${disabled ? "opacity-50" : ""}`}
    >
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${checked ? "translate-x-6" : "translate-x-1"}`} />
    </button>
  );
}

const Num = ({ label, value, unit }) => (
  <div className="rounded-lg border border-zinc-200 bg-white px-3 py-2">
    <div className="text-[11px] uppercase tracking-wide text-zinc-400">{label}</div>
    <div className="text-lg font-semibold text-zinc-900">{value}{unit ? <span className="text-xs text-zinc-400 ml-0.5">{unit}</span> : null}</div>
  </div>
);

export default function OutboxProtectionPanel() {
  const { isAdmin } = useRole();
  const [cfg, setCfg] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const load = async () => {
    try {
      const [{ data: c }, { data: m }] = await Promise.all([
        api.get("/organizations/me/whatsapp/outbox-config"),
        api.get("/organizations/me/whatsapp/outbox/metrics"),
      ]);
      setCfg(c); setMetrics(m);
    } catch (e) { setMsg(formatApiError(e.response?.data?.detail) || "Falha ao carregar."); }
  };
  useEffect(() => { load(); const t = setInterval(() => api.get("/organizations/me/whatsapp/outbox/metrics").then(({ data }) => setMetrics(data)).catch(() => {}), 20000); return () => clearInterval(t); }, []);

  const set = (k, v) => setCfg((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setBusy(true); setMsg("");
    try {
      const { data } = await api.put("/organizations/me/whatsapp/outbox-config", cfg);
      setCfg(data); setMsg("Configuração guardada.");
      await load();
    } catch (e) { setMsg(formatApiError(e.response?.data?.detail) || "Falha ao guardar."); }
    finally { setBusy(false); }
  };

  if (!cfg || !metrics) return null;
  const h = HEALTH_LABEL[metrics.provider_status] || HEALTH_LABEL.HEALTHY;

  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-6 shadow-sm space-y-5" data-testid="outbox-protection-panel">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-lg bg-zinc-950 text-white flex items-center justify-center"><ShieldCheck className="w-4.5 h-4.5" /></div>
          <div>
            <div className="font-display text-lg font-semibold text-zinc-950">Proteção do número</div>
            <div className="text-xs text-zinc-500">Controlo operacional de tráfego WhatsApp</div>
          </div>
        </div>
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${h.cls}`} data-testid="outbox-health-badge">
          <span className={`w-1.5 h-1.5 rounded-full ${h.dot}`} /> {h.txt}
        </span>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5" data-testid="outbox-metrics">
        <Num label="Em fila" value={metrics.queued} />
        <Num label="Enviadas 1h" value={metrics.sent_last_1h} />
        <Num label="Únicos 1h" value={metrics.unique_recipients_last_1h} />
        <Num label="Únicos 24h" value={metrics.unique_recipients_last_24h} />
        <Num label="A aguardar janela" value={metrics.rate_limited} />
        <Num label="Expiradas" value={metrics.expired} />
        <Num label="Falhadas 24h" value={metrics.failed_last_24h} />
        <Num label="Fallback email" value={metrics.fallback_sent} />
      </div>

      {/* Live provider rejection diagnostic — surfaces the REAL Meta error */}
      {metrics.last_error && (metrics.queued > 0 || metrics.failed_last_24h > 0) && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2.5" data-testid="outbox-last-error">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
            <div className="space-y-1 min-w-0">
              <div className="text-sm font-medium text-red-800">
                O WhatsApp está a recusar os envios{metrics.last_error_code ? ` (erro #${metrics.last_error_code})` : ""}.
              </div>
              <div className="text-xs text-red-700 break-words">{metrics.last_error}</div>
              {String(metrics.last_error_code) === "200" && (
                <div className="text-xs text-red-700">
                  O token de acesso não tem permissão para enviar mensagens nesta conta WhatsApp (WABA). Na Meta: confirme que o token / utilizador de sistema tem a permissão <strong>whatsapp_business_messaging</strong> e que a WABA está atribuída à app com <strong>acesso avançado</strong>.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Config */}
      {isAdmin ? (
        <div className="space-y-4 pt-1">
          <label className="flex items-center justify-between gap-4">
            <div>
              <div className="text-sm font-medium text-zinc-900">Modo de proteção do número novo</div>
              <div className="text-xs text-zinc-500">Garante 1 envio de cada vez e intervalo mínimo de 5–10s.</div>
            </div>
            <Toggle testid="toggle-safe-mode" checked={!!cfg.new_number_safe_mode} onChange={(v) => set("new_number_safe_mode", v)} />
          </label>

          <div className="flex items-center gap-3 flex-wrap">
            <div className="text-sm text-zinc-700">Intervalo de envio</div>
            <input type="number" min={1} value={cfg.min_interval_seconds} onChange={(e) => set("min_interval_seconds", +e.target.value)} className="w-16 border border-zinc-300 rounded-md px-2 py-1 text-sm" data-testid="input-min-interval" />
            <span className="text-zinc-400 text-sm">a</span>
            <input type="number" min={1} value={cfg.max_interval_seconds} onChange={(e) => set("max_interval_seconds", +e.target.value)} className="w-16 border border-zinc-300 rounded-md px-2 py-1 text-sm" data-testid="input-max-interval" />
            <span className="text-zinc-400 text-sm">segundos</span>
          </div>

          <label className="flex items-center justify-between gap-4">
            <div>
              <div className="text-sm font-medium text-zinc-900">Limite de destinatários únicos</div>
              <div className="text-xs text-zinc-500">Limita quantos contactos diferentes são atingidos por janela.</div>
            </div>
            <Toggle testid="toggle-unique" checked={!!cfg.unique_recipients_enabled} onChange={(v) => set("unique_recipients_enabled", v)} />
          </label>
          {cfg.unique_recipients_enabled && (
            <div className="flex items-center gap-2 flex-wrap pl-1">
              <span className="text-sm text-zinc-700">Máx.</span>
              <input type="number" min={1} value={cfg.max_unique_recipients} onChange={(e) => set("max_unique_recipients", +e.target.value)} className="w-20 border border-zinc-300 rounded-md px-2 py-1 text-sm" data-testid="input-max-unique" />
              <span className="text-sm text-zinc-700">destinatários / </span>
              <input type="number" min={1} value={cfg.unique_recipients_window_minutes} onChange={(e) => set("unique_recipients_window_minutes", +e.target.value)} className="w-20 border border-zinc-300 rounded-md px-2 py-1 text-sm" data-testid="input-unique-window" />
              <span className="text-sm text-zinc-700">min</span>
            </div>
          )}

          <label className="flex items-center justify-between gap-4">
            <div>
              <div className="text-sm font-medium text-zinc-900">Proteção automática (circuit breaker)</div>
              <div className="text-xs text-zinc-500">Pausa envios em caso de erros do provider, sem perder mensagens.</div>
            </div>
            <Toggle testid="toggle-breaker" checked={!!cfg.circuit_breaker_enabled} onChange={(v) => set("circuit_breaker_enabled", v)} />
          </label>

          <label className="flex items-center justify-between gap-4">
            <div>
              <div className="text-sm font-medium text-zinc-900">Fallback por email (mensagens críticas)</div>
              <div className="text-xs text-zinc-500">Envia por email quando o WhatsApp está indisponível (ex.: cancelamentos).</div>
            </div>
            <Toggle testid="toggle-email-fallback" checked={!!cfg.email_fallback_enabled} onChange={(v) => set("email_fallback_enabled", v)} />
          </label>

          <div className="flex items-center gap-3 pt-1">
            <button onClick={save} disabled={busy} data-testid="save-outbox-config" className="inline-flex items-center gap-2 bg-zinc-950 text-white text-sm font-medium rounded-lg px-4 py-2 disabled:opacity-50">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar
            </button>
            {msg && <span className="text-xs text-zinc-500" data-testid="outbox-config-msg">{msg}</span>}
          </div>
        </div>
      ) : (
        <div className="text-xs text-zinc-500">Só administradores podem alterar a proteção do número.</div>
      )}
    </div>
  );
}
